<template>
  <nav>
    <v-row class="fill-height" align="center" v-if="!loadingData">
      <v-col cols="12" sm="10" md="8" offset-sm="1" offset-md="2" align="center">
        <v-form ref="form" v-model="valid" @keyup.enter="createHub" @submit.prevent>
          <AlertMessages v-if="alertValue" alert-type="error" :alert-msg="alertMsg" @reset="alertValue = false" />
          <v-card max-width="800" elevation="0" rounded="xxl" color="background">
            <v-card-title class="text-h5 font-weight-bold text-center no-break justify-center mb-4">
              {{ $t('createProductTitle') }}
            </v-card-title>
            <v-card-subtitle class=" text-center justify-center mb-4 no-break">
              {{ $t('createProductSubTitle') }}
            </v-card-subtitle>
            <v-card-text>
              <v-text-field v-model="title" data-testid="title-input" solo hide-details="auto"
                class="rounded-xxl text-body-2 mb-3" :placeholder="$t('formTitlePlaceholder')" :rules="titleRules" />
              <editor-text-area-template  class="editor editor-text-area-template text-left" :content="description"
                :placeholder="$t('enterDescription')" data-testid="description-input" @updateContent="updateContent"/>
            </v-card-text>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6" lg="6" class="py-0">
                  <span class="text-body-2">
                    {{ $t('withoutTax') }}
                  </span>
                  <v-text-field 
                    v-model="formattedPrice" 
                    data-testid="price-input" 
                    solo 
                    hide-details="auto" 
                    type="number" 
                    @keypress="preventLetters"
                    class="text-body-2 mb-3 mt-3 v-text-field--rounded price-field number-field-no-arrows" 
                    :placeholder="$t('productPricePlaceholder')" 
                    :hint="$t('productPriceFreeText')" 
                    persistent-hint 
                    :rules="priceRules"
                  >
                    <template v-slot:append>
                        <v-select
                          v-model="selectedCurrency"
                          :items="currencies"
                          hide-details
                          solo
                          item-text="attributes.code"
                          item-value="attributes"
                          return-object
                          class="currency-select"
                          :menu-props="{
                            width: '50px',
                            minWidth: '50px',
                          }"
                          data-testid="currency-select"
                        />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="6" lg="6" class="py-0">
                  <span class="text-body-2">
                    {{ $t('tax') }}
                  </span>
                  <v-select
                    v-model="selectedTaxes"
                    class="tax-select mt-3"
                    :items="listTaxes" 
                    item-value="id" 
                    item-text="name" 
                    solo
                    hide-details="true"
                    append-icon="mdi-chevron-down"
                    dense
                    data-testid="tax-select" 
                  />        
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="pt-0 pb-8">
                  <span class="text-body-2">
                    {{ $t('companyInformation') }}
                  </span>
                  <v-card 
                    rounded="xxl" 
                    class="mt-3 pb-4"
                    data-testid="company-card" 
                  >
                    <v-card-text class="pb-0">
                      <v-row>
                        <v-col cols="4" offset="4">
                            <span class="text-subtitle-2 font-weight-regular">{{ $t('labelActive') }}</span>
                        </v-col>
                        <v-col cols="4">
                            <span class="text-subtitle-2 font-weight-regular">{{ $t('labelRequired') }}</span>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-text class="py-0" v-for="attribute in orderCompanyRequiredFields" :key="attribute">
                      <v-row data-testid="company-required-fields" align="center" justify="center">
                        <v-col cols="4">
                          <span class="text-subtitle-2 font-weight-bold">{{ $t(`label${attribute}`) }}</span>
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="checkboxRequiredFields"
                            color="accent"
                            class="no-colored-options mt-1 selected"
                            :data-testid="attribute + '-active-checkbox'"
                            :disabled="true"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="checkboxRequiredFields"
                            color="accent"
                            class="no-colored-options mt-1 selected"
                            :data-testid="attribute + '-required-checkbox'"
                            :disabled="true"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-text class="py-0" v-for="(attribute, cindex) in customCompanyAttributes" :key="cindex">
                      <v-row :data-testid="`company-custom-field-${cindex}`" align="center" justify="center" v-if="customCompanyAttributes.length > 0">
                        <v-col cols="4" class="text-center">
                          <v-text-field v-model="attribute.name" :placeholder="$t('customPlaceholder')" solo hide-details="auto"
                          class="rounded-xxl text-body-2 mb-3 text-center" :rules="customFieldRules"></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="attribute.active"
                            color="accent"
                            class="no-colored-options mt-1"
                            :class="{ 'selected' : attribute.active}"
                            hide-details
                            @change="setActiveForCompany(attribute)"
                            :key="resetCompanyCheckbox"
                            :readonly="true"
                            :disabled="true"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="attribute.required"
                            color="accent"
                            class="no-colored-options mt-1"
                            :class="{ 'selected' : attribute.required}"
                            :disabled="!attribute.active"
                            hide-details
                            :key="resetCompanyCheckbox"
                          />
                        </v-col>
                        <!-- <v-col style="position: absolute; left: 42%;">
                          <v-btn
                          data-testid="delete-field-button"
                          icon
                          small
                          fab 
                          rounded
                          @click.stop="removeCompanyAttribute(index)"
                        >
                          <v-icon small>
                            mdi-delete
                          </v-icon>
                        </v-btn>
                        </v-col> -->
                      </v-row>
                    </v-card-text>

                    <v-row v-if="customCompanyAttributes.length < 3">
                        <v-col cols="12" align="center" class="py-0">
                          <div class="text-center mt-5 mr-9">
                            <v-btn
                              icon
                              large
                              color="default"
                              @click="addCompanyAttribute"
                              data-testid="add-custom-field-button"
                              ><v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>
                  </v-card>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="pt-0 pb-8">
                  <span class="text-body-2">
                    {{ $t('participantInformation') }}
                  </span>
                  <v-card 
                    rounded="xxl" 
                    class="mt-3 pb-4"
                  >
                    <v-card-text class="pb-0">
                      <v-row>
                        <v-col cols="4" offset="4">
                            <span class="text-subtitle-2 font-weight-regular">{{ $t('labelActive') }}</span>
                        </v-col>
                        <v-col cols="4">
                            <span class="text-subtitle-2 font-weight-regular">{{ $t('labelRequired') }}</span>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-text class="py-0" v-for="attribute in orderParticipantsRequiredFields" :key="attribute">
                      <v-row align="center" justify="center">
                        <v-col cols="4">
                          <span class="text-subtitle-2 font-weight-bold">{{ $t(`label${attribute}`) }}</span>
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="checkboxRequiredFields"
                            color="accent"
                            class="no-colored-options mt-1 selected"
                            :data-testid="attribute + '-active-checkbox'"
                            :disabled="true"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="checkboxRequiredFields"
                            color="accent"
                            class="no-colored-options mt-1 selected"
                            :data-testid="attribute + '-required-checkbox'"
                            :disabled="true"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-text class="py-0" v-for="attribute in availableAdditionalOrderParticipantAttributes" :key="attribute">
                      <v-row align="center" justify="center" v-if="availableAdditionalOrderParticipantAttributes.length > 0">
                        <v-col cols="4">
                          <span class="text-subtitle-2 font-weight-bold">{{ $t(attribute) }}</span>
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="additionalAttributesForParticipants[attribute].active"
                            color="accent"
                            class="no-colored-options mt-1"
                            :class="{ 'selected' : additionalAttributesForParticipants[attribute].active}"
                            :data-testid="attribute + '-active-checkbox'"
                            hide-details
                            @change="setActive(attribute)"
                            :key="resetCheckbox"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="additionalAttributesForParticipants[attribute].required"
                            color="accent"
                            class="no-colored-options mt-1"
                            :class="{ 'selected' : additionalAttributesForParticipants[attribute].required}"
                            :data-testid="attribute + '-required-checkbox'"
                            :disabled="!additionalAttributesForParticipants[attribute].active"
                            hide-details
                            :key="resetCheckbox"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>

                    <v-card-text class="py-0" v-for="(attribute, pindex) in customParticipantAttributes" :key="pindex">
                      <v-row :data-testid="`participant-custom-field-${pindex}`" align="center" justify="center" v-if="customParticipantAttributes.length > 0">
                        <v-col cols="4" class="text-center">
                          <v-text-field v-model="attribute.name" :placeholder="$t('customPlaceholder')" solo hide-details="auto"
                          class="rounded-xxl text-body-2 mb-3 text-center" :rules="customFieldRules"></v-text-field>

                          <v-checkbox
                            v-model="attribute.is_file_upload"
                            :label="$t('labelIsFileUpload')"
                            color="accent"
                            class="no-colored-options mt-1 file-option-selection"
                            :class="{ 'selected' : attribute.is_file_upload}"
                            hide-details
                            :key="'file'+pindex"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="attribute.active"
                            color="accent"
                            class="no-colored-options mt-1"
                            :class="{ 'selected' : attribute.active}"
                            hide-details
                            @change="setActiveForParticipant(attribute)"
                            :key="resetParticipantCheckbox"
                            :readonly="true"
                            :disabled="true"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-checkbox
                            style="width:26px"
                            v-model="attribute.required"
                            color="accent"
                            class="no-colored-options mt-1"
                            :class="{ 'selected' : attribute.required}"
                            :disabled="!attribute.active"
                            hide-details
                            :key="resetParticipantCheckbox"
                          />
                        </v-col>
                        <!-- <v-col style="position: absolute; left: 42%;">
                          <v-btn
                          data-testid="delete-field-button"
                          icon
                          small
                          fab 
                          rounded
                          @click.stop="removeParticipantAttribute(index)"
                        >
                          <v-icon small>
                            mdi-delete
                          </v-icon>
                        </v-btn>
                        </v-col> -->
                      </v-row>
                    </v-card-text>

                    <v-row v-if="customParticipantAttributes.length < 3">
                        <v-col cols="12" align="center" class="py-0">
                          <div class="text-center mt-5 mr-9">
                            <v-btn
                              icon
                              large
                              color="default"
                              @click="addParticipantAttribute"
                              data-testid="add-participant-custom-field-button"
                              ><v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>

                  </v-card>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="12" sm="12"  align="center">
                    <v-tabs
                      v-model="sessionType"
                      :color="'var(--v-accent-base)'"
                      centered
                      fixed-tabs
                      :slider-color="'var(--v-accent-base)'"
                      class="overflow-hidden"
                    >
                      <v-tab
                        class="text-none"
                        data-testid="session-single"
                        href="#single"
                        style="border-bottom: 2px solid var(--v-disabled-base);"
                      >
                        {{ $t('singleSession') }} 
                      </v-tab>
                      <v-tab
                        class="text-none"
                        data-testid="session-multiple"
                        href="#multiple"
                        style="border-bottom: 2px solid var(--v-disabled-base);"
                      >
                        {{ $t('multipleSession') }} 
                      </v-tab>
            
                      <v-tabs-items 
                        v-model="sessionType"
                      >
                        <v-tab-item
                          :transition="false"
                          reverse-transition="false"
                          value="single"
                          key="single"
                        >
                          <v-row class="mt-5" v-if="sessionType == 'single'">
                            <v-col cols="12" md="12" lg="12" class="py-0">
                             
                                  <v-autocomplete 
                                    :key="sessionType + '-single-autocomplete'"
                                    v-model="selectedProduct"
                                    :menu-props="{
                                      closeOnContentClick: true,
                                      rounded: 'xxl',
                                      zIndex: '9999 !important',
                                    }" 
                                    filled 
                                    rounded 
                                    deletable-chips 
                                    clearable 
                                    solo 
                                    no-filter 
                                    :search-input.sync="searchKeyword" 
                                    @click="searchKeyword=''"
                                    :items="entries" 
                                    :placeholder="$t('productSearchActivityHubWorkflowPlaceholder')" 
                                    append-icon="mdi-magnify"
                                    background-color="#fff" 
                                    data-testid="entity-select" 
                                    class="expanding-search text-body-2 mt-3" 
                                    return-object 
                                    :error-messages="errorsProduct"
                                  >
                                    <template v-slot:no-data>
                                      <p class="mx-5 mt-4">
                                        {{ $t('noDataText') }}
                                      </p>
                                    </template>
                                    <template v-slot:selection="data">
                                      <span class="pl-2">{{ data.item.attributes.title }}</span>
                                    </template>
                                    <template v-slot:item="data" data-testid="suggested-item">
                                        <template v-if="(typeof data.item) !== 'object'">
                                            <v-list-item-content v-text="data.item" />
                                        </template>
                                        <template v-else>
                                            <v-row no-gutters align="center">
                                          
                                                <v-col cols="12">
                                                <v-list-item-content>
                                                    <v-list-item-title class="text-no-wrap text-truncate px-2" style="width:280px">
                                                      <v-chip v-if="data.item.type == 'hubs'" small color="accent">
                                                          <span class="text-uppercase">{{ $t('hub') }}</span>
                                                      </v-chip>
                                                      <v-chip v-if="data.item.type == 'workflows'" small color="accent">
                                                          <v-icon small>mdi-sitemap</v-icon>
                                                      </v-chip>
                                                      {{ data.item.attributes.title }}
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                              </v-col>
                                            </v-row>
                                        </template>
                                    </template>
                                    <template v-slot:prepend-inner>
                                      <v-progress-circular
                                        v-if="loadingEntities"
                                        size="20"
                                        indeterminate
                                      />
                                    </template>
                                  </v-autocomplete>
                            </v-col>
                          </v-row>    
                          
                          <v-row>
                            <v-col cols="12" class="py-0">
                              <span class="text-body-2">
                                {{ $t('labelParticipantsLimit') }}
                              </span>
                              <v-text-field 
                                v-model="participantsLimit" 
                                data-testid="participants-limit-input" 
                                solo hide-details="auto" 
                                type="number" 
                                @keypress="preventLetters"
                                class="text-body-2 mt-3 mb-3 v-text-field--rounded number-field-no-arrows" 
                                :hint="$t('productParticipantsLimitText')" 
                                persistent-hint
                                :rules="participantsLimitRules"
                              ></v-text-field>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col  cols="12" md="12" lg="12" class="py-0">
                                   <!-- Date Picker -->
                                  <v-menu
                                    v-model="dateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                  >
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-text-field
                                        v-model="end_date"
                                        :label="$t('sessionEndDate')"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                    
                                      <template v-slot:prepend>
                                        <v-icon color="accent">mdi-calendar</v-icon>
                                      </template>
                                      <template v-slot:label>
                                        <div>
                                          <span
                                            class="text-subtitle-2 accent--text"
                                            >{{ $t("chooseEndDate") }}</span
                                          >
                                        </div>
                                      </template>
                                    </v-text-field>
                                    </template>
                                    <v-date-picker
                                      v-model="end_date"
                                      :min="minDate"
                                      no-title
                                      @input="dateMenu = false"
                                    ></v-date-picker>
                                  </v-menu>
                            </v-col>
                          </v-row>


                        </v-tab-item>
                    
                        <v-tab-item
                          :transition="false"
                          reverse-transition="false"
                          value="multiple"
                          key="multiple"
                        >

                          <div data-testid="multiple-session-area" class="mt-5">
                            <div v-for="(session, index) in sessions" :key="index">
                              <v-row class="mt-4" v-if="sessionType == 'multiple'">
                                <v-col cols="12" md="12" lg="12" class="py-0">
                                    <v-text-field 
                                      v-model="session.session_title"
                                      data-testid="session_title-input" solo hide-details="auto"
                                      class="rounded-xxl text-body-2 mb-3" :placeholder="$t('formsessionTitlePlaceholder')" :rules="titleRules" 
                                    ></v-text-field>
                                  </v-col>
                                <v-col cols="12" md="12" lg="12" class="py-0">
                                  
                                      <v-autocomplete 
                                        :key="sessionType + '-multiple-autocomplete-' + index"
                                        :data-testid="sessionType + '-product-' + index"
                                        v-model="session.product"
                                        :menu-props="{
                                          closeOnContentClick: true,
                                          rounded: 'xxl',
                                          zIndex: '9999 !important',
                                        }" 
                                        @change="checkSession(index)"
                                        filled 
                                        rounded 
                                        deletable-chips 
                                        clearable 
                                        solo 
                                        no-filter 
                                        :search-input.sync="session.searchKeyword"
                                        @update:search-input="val => onSearchKeywordChange(val, index)"
                                        :items="session.entries"
                                        :loading="session.loadingEntities"
                                        @click="session.searchKeyword = ''"
                                        :placeholder="$t('productSearchActivityHubWorkflowPlaceholder')" 
                                        append-icon="mdi-magnify"
                                        background-color="#fff" 
                                        class="expanding-search text-body-2 mt-3" 
                                        return-object 
                                        :error-messages="session.error"
                                      >
                                        <template v-slot:no-data>
                                          <p class="mx-5 mt-4">
                                            {{ $t('noDataText') }}
                                          </p>
                                        </template>
                                        <template v-slot:selection="data">
                                          <span class="pl-2">{{ data.item.attributes.title }}</span>
                                        </template>
                                        <template v-slot:item="data" data-testid="suggested-item">
                                            <template v-if="(typeof data.item) !== 'object'">
                                                <v-list-item-content v-text="data.item" />
                                            </template>
                                            <template v-else>
                                                <v-row no-gutters align="center">
                                              
                                                    <v-col cols="12">
                                                    <v-list-item-content>
                                                        <v-list-item-title class="text-no-wrap text-truncate px-2" style="width:280px">
                                                          <v-chip v-if="data.item.type == 'hubs'" small color="accent">
                                                              <span class="text-uppercase">{{ $t('hub') }}</span>
                                                          </v-chip>
                                                          <v-chip v-if="data.item.type == 'workflows'" small color="accent">
                                                              <v-icon small>mdi-sitemap</v-icon>
                                                          </v-chip>
                                                          {{ data.item.attributes.title }}
                                                        </v-list-item-title>
                                                    </v-list-item-content>
                                                  </v-col>
                                                </v-row>
                                            </template>
                                        </template>
                                        <template v-slot:prepend-inner>
                                          <v-progress-circular
                                            v-if="session.loadingEntities"
                                            size="20"
                                            indeterminate
                                          />
                                        </template>
                                      </v-autocomplete>
                                </v-col>
                              
                                <v-col cols="12"  md="12" lg="12" class="py-0">
                                  <span class="text-body-2">
                                    {{ $t('labelParticipantsLimit') }}
                                  </span>
                                  <v-text-field 
                                    v-model="session.participants_limit" 
                                    data-testid="participants-limit-input" 
                                    solo hide-details="auto" 
                                    type="number" 
                                    @keypress="preventLetters"
                                    class="text-body-2 mt-3 mb-3 v-text-field--rounded number-field-no-arrows" 
                                    :rules="participantsLimitRules"
                                  >
                                  <template v-slot:append-outer>
                                      <v-btn
                                        v-if="sessions.length > 1"
                                        icon
                                        small
                                        color="error"
                                        @click="removeSession(index)"
                                        ><v-icon>mdi-delete-circle</v-icon></v-btn
                                      >
                                    </template>
                                  </v-text-field>
                                </v-col>
                                <v-col  cols="12" md="12" lg="12" class="py-0">
                                   <!-- Date Picker -->
                                  <v-menu
                                    v-model="session.dateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                  >
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-text-field
                                        v-model="session.end_date"
                                        :label="$t('sessionEndDate')"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                    
                                      <template v-slot:prepend>
                                        <v-icon color="accent">mdi-calendar</v-icon>
                                      </template>
                                      <template v-slot:label>
                                        <div>
                                          <span
                                            class="text-subtitle-2 accent--text"
                                            >{{ $t("chooseEndDate") }}</span
                                          >
                                        </div>
                                      </template>
                                    </v-text-field>
                                    </template>
                                    <v-date-picker
                                      v-model="session.end_date"
                                      :min="minDate"
                                      no-title
                                      @input="session.dateMenu = false"
                                    ></v-date-picker>
                                  </v-menu>
                                </v-col>
                              </v-row>
                              <hr>
                            </div>

                            <v-row v-if="sessions.length < 25">
                              <v-col cols="12" align="center" class="py-0">
                                <div class="text-center mt-5 mr-9">
                                  <v-btn
                                    icon
                                    large
                                    color="default"
                                    @click="addSession"
                                    data-testid="add-session-button"
                                    ><v-icon>mdi-plus</v-icon>
                                  </v-btn>
                                </div>
                              </v-col>
                            </v-row>
                          </div>

                        </v-tab-item>
                      </v-tabs-items>
                    </v-tabs>
                  </v-col>
                </v-row>
            </v-card-text>
          </v-card>
          <v-row>
            <v-col cols="10" md="6" sm="10" offset-md="3" offset="1" align="center">
               <span class="text-body-2 greytext--text">
              {{ $t('productImage') }}
            </span>
              <v-hover>
                <template v-slot:default="{ hover }">
                  <v-card data-testid="product-cover-card" rounded="md" class="mx-auto pa-0 rounded-xxl mt-3"
                    :elevation="hover ? 8 : 5" max-width="600">
                    <v-card-actions class="pa-0 ">
                      <v-img class="rounded-xxl" max-height="280" :height="coverURL == null ? '280' : 'auto'" :src="coverURL" data-testid="cover-image"/>
                        <div class="center-button-on-image">
                        <v-btn :loading="loadingCover" data-testid="upload-cover-button" mx-auto color="accent"
                          class="rounded-xxl text-none px-10" elevation="2" relative bottom left @click="showUploadCover">
                          {{ $t('upload') }}
                        </v-btn>
                        <v-btn data-testid="upload-unsplash-cover-button" mx-auto color="accent"
                          class="rounded-xxl text-none px-10 d-block mx-auto mt-3" :class="{'disable-events': loadingCover}" elevation="2" relative bottom left @click="toggleUnsplash">
                          {{ $t('browse') }}
                        </v-btn>
                      </div>
                    </v-card-actions>
                  </v-card>
                </template>
              </v-hover>
              <v-file-input ref="file_input_cover" v-model="coverImageData" accept="image/png, image/jpeg"
                data-testid="cover-file-input" class="d-block product-cover-image" @change="uploadCover" :rules="coverPath != null ? [] : coverRules"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col align="center">
              <FormFooter next-button-text="create" :loading="loading" @proceedSubmit="createProduct" />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <UnsplashSearch 
      v-if="showUnsplash"
      ref="unsplashDialog"
      :visible="showUnsplash"
      data-testid="unsplash-dialog"
      @close="showUnsplash = false"
      @applyPhoto="embedUnsplash"
    />
  </nav>
</template>
<script>
import { ruleRequired, ruleValidMin2Max70Characters, priceRuleValidMax, ruleRequiredCover, limitParticipantsRuleValidMax } from '../../rules/formRules.js';
import i18n from '../../plugins/i18n'
import { getCurrencies, getAvailableAdditionalOrderParticipantAttributes, createProduct, uploadCoverImage, createProductWithSessions } from '../../api/productApi'
import FormFooter from '../../components/FormFooter'
import AlertMessages from '../../components/AlertMessages'
import UnsplashSearch from '@/components/unsplash/UnsplashSearch.vue';
import EditorTextAreaTemplate from '../../components/EditorTextAreaTemplate';
import { searchActivitiesIsBooster } from '../../api/activityApi'
import { searchHubsIsBooster } from '../../api/hubApi'
import { searchWorkflowsIsBooster } from '../../api/workflowApi'

import _ from 'lodash'

export default {
  components: {
    FormFooter, 
    AlertMessages,
    UnsplashSearch,
    EditorTextAreaTemplate
  },
  data() {
    return {
      valid: true,
      title: '',
      titleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
      customFieldRules: ruleRequired('labelCustomField').concat(ruleValidMin2Max70Characters('labelCustomField')),
      description: null,
      alertMsg: '',
      buttonDisabled: false,
      coverImageData: null,
      coverURL: null,
      coverPath: null,
      loadingCover: false,
      alertValue: false,
      spaceId: window.$cookies.get('space_id'),
      userId: window.$cookies.get('id'),
      showUnsplash: false,
      price: 0,
      selectedCurrency: {id:1, attributes: {name: 'Swedish Krona', code: 'SEK', symbol: 'kr'}},
      currencies: [],
      loadingData: false,
      select: null,
      searchKeyword: null,
      searchKeywordMultiple: '',
      entries: [],
      selectedProduct: null,
      errorsProduct: [],
      coverRules: ruleRequiredCover('labelCover'),
      loadingEntities: false,
      priceRules: priceRuleValidMax,
      listTaxes: [
          {
              id: 0,
              name: '0%'
          },
          {
              id: 6,
              name: '6%'
          },
          {
              id: 12,
              name: '12%'
          },
          {
              id: 25,
              name: '25%'
          }
      ],
      selectedTaxes: 0,
      availableAdditionalOrderParticipantAttributes: [],
      additionalAttributesForParticipants: {},
      resetCheckbox: null,
      participantsLimit: null,
      participantsLimitRules: limitParticipantsRuleValidMax,
      checkboxRequiredFields: true,
      orderParticipantsRequiredFields: ['FirstName', 'LastName', 'Email'],
      orderCompanyRequiredFields: ['CompanyName', 'OrganizationNumber', 'CompanyAddress', 'City', 'PostCode'],
      customCompanyAttributes: [],
      resetCompanyCheckbox: null,
      customParticipantAttributes: [],
      resetParticipantCheckbox: null,
      sessionType: 'single',
      end_date: null,
      dateMenu: false,
      minDate: new Date().toISOString().substr(0, 10),
      sessions: [
        {
          error: [],
          product: null,
          session_title: "",
          participants_limit: "",
          searchKeyword: '', 
          entries: [],
          loadingEntities: false,
          end_date: null,
          dateMenu: false
        },
      ]
    }
  },
  computed: {
    loading() {
      if (this.loadingCover || this.buttonDisabled) {
        return true
      }
      return false
    },
    productPlan() {
      return this.$store.state.productPlan;
    },
     formattedPrice: {
      get() {
        return this.price.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true, style: 'decimal' }).replace(',', '.');
      },
      set(value) {
        this.price = parseFloat(value.replace('.', ''));
      },
    },
  },
  watch: {
    searchKeyword(val) {
      if (val == null || val == '') {
        this.entries = []
        return
      }
      let searchkey = val.trim()
      if (searchkey.length < 2) return
      this.searchActivitiesHubsAndWorkflows(searchkey)
    },
    selectedProduct(val){
      if(val != null){
        this.errorsProduct = []
      }
    },
  },

  async created(){
    this.loadingData = true
    await this.fetchCurrencies();
    await this.fetchAvailableAdditionalOrderParticipantAttributes();
    this.loadingData = false
  },
  methods: {
    addSession() {
      this.sessions.push({
        error: [],
        product: null,
        session_title: "",
        participants_limit: "",
        searchKeyword: '', 
        entries: [],
        loadingEntities: false,
        end_date: null,
        dateMenu: false,
      });
    },
    checkSession(index) {
      this.sessions[index].error = []
      if (this.sessions[index].product == null) {
        this.sessions[index].error.push(i18n.t("productErrorRequired"))
      } else {
        this.sessions[index].error = []
      }
    },
    removeSession(index) {
      this.sessions.splice(index, 1);
    },
    onSearchKeywordChange(val, index) {
      if (!val || val.trim().length < 2) {
        this.sessions[index].entries = [];
        return;
      }
      this.searchActivitiesHubsAndWorkflows(val, index);
    },
    async fetchCurrencies(){
        try {
            const response = await getCurrencies();
            if(response.status == 200) {
                this.currencies = response.data.data;
            }                
        } catch (e) {
            if (e.response) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
            }
        }
    },
    async fetchAvailableAdditionalOrderParticipantAttributes(){
        try {
            const response = await getAvailableAdditionalOrderParticipantAttributes();
            if(response.status == 200) {
                this.availableAdditionalOrderParticipantAttributes = response.data.meta.additional_order_participant_attributes;
                this.availableAdditionalOrderParticipantAttributes.forEach(attribute => {
                  this.additionalAttributesForParticipants[attribute] = {required: false, active: false}
                })
            }                
        } catch (e) {
            if (e.response) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
            }
        }
    },
    async createProduct() {
      this.$refs.form.validate()
      if (this.sessionType == "single") {
        if(this.selectedProduct != null && this.valid) {
          this.buttonDisabled = true
        }
      } else if (this.sessionType === "multiple") {
        this.errorsProduct = [];
       
        this.sessions.forEach(session => {
          session.error = [];
          if(session.product == null) {
            session.error.push(i18n.t("productErrorRequired"));
            return false;
          }
        })
       
      }
      if (this.valid) {
        
        this.buttonDisabled = true

        let additionalAttributes = JSON.parse(JSON.stringify(this.additionalAttributesForParticipants))
        let additionalAttributesFinal = {}

        Object.keys(additionalAttributes).forEach(function(key) {
            if (additionalAttributes[key].active) {
              additionalAttributesFinal[key] = {'required': additionalAttributes[key].required}   
            }
        });

        additionalAttributesFinal = JSON.parse((JSON.stringify(additionalAttributesFinal)))

        try {
          let data = {
            data: {
              type: "store-products",
              attributes: {
                title: this.title.trim(),
                image: this.coverPath,
                description: this.description == null || this.description == '<p></p>' ? null : this.description,
                price: this.price == null || this.price == '' ? 0 : parseInt(this.price),
                tax: this.selectedTaxes,
                currency_id: parseInt(this.selectedCurrency.id),
                participants_limit: this.participantsLimit === null ? null : parseInt(this.participantsLimit),
                additional_attributes_for_participants: additionalAttributesFinal,
                session_type: this.sessionType,
                end_date: this.end_date !== null ? new Date(this.end_date) : null,
                company_custom_attributes: this.customCompanyAttributes,
                participant_custom_attributes: this.customParticipantAttributes,
              },
              relationships: {
                space: {
                  data: {
                    type: "spaces",
                    id: "" + this.spaceId + ""
                  },
                }
              }
            }
          };

          if (this.sessionType === "multiple") {
              delete data.data.relationships.productable;
              delete data.data.attributes.participants_limit;
              delete data.data.attributes.end_date;

              data.data.relationships.sessions = {
                data: this.sessions.map(session => ({
                  type: "store-product-sessions",
                  attributes: {
                      session_title: session.session_title,
                      end_date: session.end_date,
                      participants_limit: session.participants_limit === null ? null : parseInt(session.participants_limit)
                  },
                  productable: {
                      data: {
                          type: session.product.type,
                          id: session.product.id
                      }
                  }
              }))
            };
            
          } else {
              data.data.relationships.productable = {
                  data: {
                      type: this.selectedProduct.type,
                      id: this.selectedProduct.id
                  }
              };
          }

          var response;
          if (this.sessionType !== "multiple") {
              response = await createProduct(data);
          } else {
              response = await createProductWithSessions(data);
          }
          if (response) {
            if (response.status == 201) {
              this.$router.push({ name: 'StoreProducts', params: { id: response.data.data.id, successfullyCreatedStoreProduct: true } })
            }
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true
            if (e.response.status == 403) {
              this.$router.push({ name: 'PageAccessDenied' })
            } else {
              this.alertValue = true
              this.alertMsg = i18n.t('serverError')
            }
          } else {
            this.alertMsg = i18n.t('serverError')
          }
        }
        this.buttonDisabled = false
      } else if (this.selectedProduct == null && this.sessionType == "single") {
        this.errorsProduct = [i18n.t('fieldErrorRequired', { 'field': i18n.t('labelProduct') })]
      }
    },
    async uploadCover() {
      if (this.coverImageData.size > 5 * 1024 * 1024) {
        this.alertMsg = i18n.t('uploadCoverError')
        this.alertValue = true
      } else {
        this.loadingCover = true
        try {
          let data = new FormData();
          data.append('file', this.coverImageData);
          data.append('type', 'cover');
          data.append('space_id', this.spaceId);

          const response = await uploadCoverImage(data)
          if (response.status == 200) {
            this.loadingCover = false
            this.alertMsg = ''
            this.coverURL = URL.createObjectURL(this.coverImageData)
            this.coverPath = response.data.meta.detail
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true
            if (e.response.status == 422) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                if (e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize') {
                  this.alertMsg = i18n.t('uploadCoverError')
                } else {
                  this.alertMsg = i18n.t('serverError')
                }
              }
            } else if (e.response.status == 403) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                if (e.response.data.errors[i].detail == 'Storage is full') {
                  this.alertMsg = i18n.t('storageFullError')
                }
                else {
                  this.$router.push({ name: 'PageAccessDenied' })
                }
              }
            } else {
              this.alertMsg = i18n.t('serverError')
            }
          } else {
            this.alertValue = true
            this.alertMsg = i18n.t('serverError')
          }
          this.loadingCover = false
        }
      }
    },
     updateContent(content) {
      if (content != '') {
        this.description = content
      }
    },
         

    setActive(attribute) {
      this.additionalAttributesForParticipants[attribute].required = false
      this.resetCheckbox = Date.now()
    }, 

    showUploadCover() {
      this.$refs.file_input_cover.$refs.input.click()
    },

    toggleUnsplash(){
      this.showUnsplash = !this.showUnsplash
    },

    embedUnsplash(photo){
      this.showUnsplash = false
      this.coverImageData = photo
      this.coverURL = photo.urls.regular
      this.coverPath = photo.urls.regular
    },

    preventLetters(e) {
      e = e || window.event;
      var charCode = (typeof e.which == "undefined") ? e.keyCode : e.which;
      var charStr = String.fromCharCode(charCode);

      if (!charStr.match(/^[0-9]+$/))
        e.preventDefault();
        
      if (e.target.value >= 999999999)
        e.preventDefault();
    },
    handleSearchKeywordChange(val) {
      if (val == null || val == '') {
        this.entries = [];
        return;
      }
      const searchKey = val.trim();
      if (searchKey.length < 2) return;
      this.searchActivitiesHubsAndWorkflows(searchKey);
    },


    searchActivitiesHubsAndWorkflows:_.debounce(async function(keyword, index ) {
      if(index != undefined){
        this.sessions[index].loadingEntities = true;
      } else {
        this.loadingEntities = true
      }
      var searchActivities = []
      var searchHubs = []
      var searchWorkflows = []
      if (keyword && keyword.trim().length >= 2) {
        try {
          try {
            const response = await searchActivitiesIsBooster(this.spaceId, keyword)
            if (response) {
              if (response.status == 200) {
                searchActivities = response.data.data
              }
            }
          } catch (e) {
            if (e.response) {
              this.error = true
              this.alertType = 'error'
              this.alertMsg = i18n.t('serverError')
              this.alertValue = true
            }
          }
          if (this.productPlan.attributes && this.productPlan.attributes.name != 'Free') {
            try {
              const response = await searchHubsIsBooster(this.spaceId, keyword)
              if (response) {
                if (response.status == 200) {
                  searchHubs = response.data.data
                }
              }
            } catch (e) {
              if (e.response) {
                this.error = true
                this.alertType = 'error'
                this.alertMsg = i18n.t('serverError')
                this.alertValue = true
              }
            }
            if(this.productPlan.attributes.workflow_module == 1){
              try {
                const response = await searchWorkflowsIsBooster(this.spaceId, keyword)
                if (response) {
                  if (response.status == 200) {
                    searchWorkflows = response.data.data
                  }
                }
              } catch (e) {
                if (e.response) {
                  this.error = true
                  this.alertType = 'error'
                  this.alertMsg = i18n.t('serverError')
                  this.alertValue = true
                }
              }
            }
          }
          Array.prototype.push.apply(searchActivities, searchHubs)
          Array.prototype.push.apply(searchActivities, searchWorkflows)
          searchActivities.sort(function (a, b) { return (a.sort_term > b.sort_term) ? 1 : ((b.sort_term > a.sort_term) ? -1 : 0) })
        
          if(index != undefined){
            this.$set(this.sessions[index], 'entries', [...searchActivities]);
          }else{
            this.entries = [...searchActivities];
          }
          this.$forceUpdate();
        } catch (error) {
        console.log(error)
        } finally {
          if (index != undefined) {
            this.sessions[index].loadingEntities = false;
          } else {
            this.loadingEntities = false;
          }
        }
      }
    }, 500),

    addCompanyAttribute() {
      this.customCompanyAttributes.push({
        name: '',
        required: false,
        active: true
      })
    },
    removeCompanyAttribute(index) {
      this.customCompanyAttributes.splice(index, 1);
    },
    
    setActiveForCompany(attribute) {
      attribute.required = false
      this.resetCompanyCheckbox = Date.now()
    }, 
    addParticipantAttribute() {
      this.customParticipantAttributes.push({
        name: '',
        is_file_upload: false,
        required: false,
        active: true
      })
    },
    removeParticipantAttribute(index) {
      this.customParticipantAttributes.splice(index, 1);
    },
    
    setActiveForParticipant(attribute) {
      attribute.required = false
      this.resetParticipantCheckbox = Date.now()
    }, 
  }
}
</script>
<style scoped>
.v-input.text-center input {
    text-align: center;
}
.file-option-selection.v-input--selection-controls .v-input__slot > .v-label {
    font-size: small;
}
</style>
